/**
 * Storage Cleanup Utility
 * 
 * Helps manage localStorage usage and clean up when quota is exceeded.
 */

// Keys to check and their priority (lower = clear first)
const STORAGE_KEYS_PRIORITY = [
    { key: 'ai_training_feedback', priority: 1, critical: false },
    { key: 'feedback_reward_data', priority: 2, critical: false },
    { key: 'ledger_smart_cache', priority: 3, critical: false },
    { key: 'ledger_generated_materials', priority: 4, critical: false },
    { key: 'ledger_promo_effects', priority: 5, critical: false },
    { key: 'ledger_sessions_backup', priority: 5, critical: false }, // Backup can be removed
    { key: 'luca_feedback_training', priority: 5, critical: false },
    { key: 'ledger_sessions', priority: 6, critical: true },
    { key: 'ledger_current_user', priority: 10, critical: true },
    { key: 'ledger_users', priority: 10, critical: true },
    { key: 'ledger_config', priority: 10, critical: true },
];

/**
 * Get localStorage usage statistics
 */
export function getStorageStats(): {
    used: number;
    total: number;
    percentUsed: number;
    itemCount: number;
    largestItems: Array<{ key: string; size: number }>;
} {
    let used = 0;
    let itemCount = 0;
    const items: Array<{ key: string; size: number }> = [];
    
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key) {
                const value = localStorage.getItem(key) || '';
                const size = (key.length + value.length) * 2; // UTF-16 = 2 bytes per char
                used += size;
                itemCount++;
                items.push({ key, size });
            }
        }
    } catch (e) {
        console.error('[StorageCleanup] Error getting storage stats:', e);
    }
    
    // localStorage limit is typically 5MB
    const total = 5 * 1024 * 1024;
    const percentUsed = Math.round((used / total) * 100);
    
    // Sort by size descending
    items.sort((a, b) => b.size - a.size);
    
    return {
        used,
        total,
        percentUsed,
        itemCount,
        largestItems: items.slice(0, 10) // Top 10 largest
    };
}

/**
 * Clean up non-critical storage to free space
 */
export function cleanupNonCriticalStorage(): boolean {
    console.log('[StorageCleanup] Cleaning up non-critical storage...');
    
    const statsBefore = getStorageStats();
    console.log(`[StorageCleanup] Before cleanup: ${formatSize(statsBefore.used)} used (${statsBefore.percentUsed}%)`);
    
    let freedSpace = 0;
    
    // Sort by priority (clear low priority first)
    const keysToCheck = [...STORAGE_KEYS_PRIORITY].sort((a, b) => a.priority - b.priority);
    
    for (const { key, critical } of keysToCheck) {
        if (critical) continue; // Skip critical data
        
        try {
            const value = localStorage.getItem(key);
            if (value) {
                const size = (key.length + value.length) * 2;
                localStorage.removeItem(key);
                freedSpace += size;
                console.log(`[StorageCleanup] Removed ${key} (${formatSize(size)})`);
            }
        } catch (e) {
            console.error(`[StorageCleanup] Failed to remove ${key}:`, e);
        }
    }
    
    const statsAfter = getStorageStats();
    console.log(`[StorageCleanup] After cleanup: ${formatSize(statsAfter.used)} used (${statsAfter.percentUsed}%)`);
    console.log(`[StorageCleanup] Freed ${formatSize(freedSpace)}`);
    
    return freedSpace > 0;
}

/**
 * Emergency cleanup - removes even large critical items, keeping only essential user data
 */
export function emergencyCleanup(): void {
    console.warn('[StorageCleanup] EMERGENCY CLEANUP - Removing large items');
    
    // Always remove sessions backup first - it's a huge space saver
    try {
        const backupSize = localStorage.getItem('ledger_sessions_backup');
        if (backupSize) {
            localStorage.removeItem('ledger_sessions_backup');
            console.log(`[StorageCleanup] Emergency removed ledger_sessions_backup (${formatSize(backupSize.length * 2)})`);
        }
    } catch (e) {
        console.error('[StorageCleanup] Failed to remove sessions backup:', e);
    }
    
    const stats = getStorageStats();
    
    // Remove items larger than 500KB
    for (const item of stats.largestItems) {
        if (item.size > 500 * 1024) {
            try {
                // Check if it's a critical key
                const isCritical = STORAGE_KEYS_PRIORITY.find(k => k.key === item.key)?.critical ?? false;
                
                if (!isCritical) {
                    localStorage.removeItem(item.key);
                    console.log(`[StorageCleanup] Emergency removed ${item.key} (${formatSize(item.size)})`);
                } else if (item.key === 'ledger_sessions') {
                    // For sessions, try to truncate instead of remove
                    truncateSessions();
                }
            } catch (e) {
                console.error(`[StorageCleanup] Failed to remove ${item.key}:`, e);
            }
        }
    }
}

/**
 * Truncate sessions to free space while keeping recent data
 * IMPROVED: Higher limits to preserve more content
 */
function truncateSessions(): void {
    try {
        const sessionsRaw = localStorage.getItem('ledger_sessions');
        if (!sessionsRaw) return;
        
        const sessions = JSON.parse(sessionsRaw);
        
        if (!Array.isArray(sessions)) return;
        
        // Sort by last modified, keep newest 20 sessions (was 10)
        const sorted = sessions.sort((a: any, b: any) => (b.lastModified || 0) - (a.lastModified || 0));
        const truncated = sorted.slice(0, 20).map((session: any) => ({
            ...session,
            // Keep last 20 messages per session (was 10)
            messages: (session.messages || []).slice(-20).map((msg: any) => ({
                ...msg,
                // Truncate only messages over 50k chars (was 10k)
                content: msg.content?.length > 50000 
                    ? msg.content.substring(0, 50000) + '\n\n... [Content shortened due to storage limits]'
                    : msg.content
            }))
        }));
        
        localStorage.setItem('ledger_sessions', JSON.stringify(truncated));
        console.log('[StorageCleanup] Truncated sessions to 20 most recent');
    } catch (e) {
        console.error('[StorageCleanup] Failed to truncate sessions:', e);
    }
}

/**
 * Check if storage is getting full and proactively clean up
 * Optimized thresholds to avoid premature cleanup
 */
export function checkAndCleanupIfNeeded(): void {
    const stats = getStorageStats();
    
    if (stats.percentUsed >= 98) {
        // Only emergency cleanup at critical levels
        console.warn(`[StorageCleanup] Storage at ${stats.percentUsed}% - EMERGENCY cleanup`);
        emergencyCleanup();
        cleanupNonCriticalStorage();
    } else if (stats.percentUsed >= 92) {
        // Normal cleanup at 92% (was 80% - too aggressive)
        cleanupNonCriticalStorage();
    }
    // Below 92% - let storage usage be natural
}

/**
 * Safe localStorage setItem that handles quota exceeded
 */
export function safeSetItem(key: string, value: string): boolean {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
            console.warn(`[StorageCleanup] Quota exceeded while setting ${key}, cleaning up...`);
            
            // Try cleanup and retry
            if (cleanupNonCriticalStorage()) {
                try {
                    localStorage.setItem(key, value);
                    return true;
                } catch (retryError) {
                    // Still failing, try emergency cleanup
                    emergencyCleanup();
                    try {
                        localStorage.setItem(key, value);
                        return true;
                    } catch (finalError) {
                        console.error(`[StorageCleanup] Failed to save ${key} even after cleanup`);
                        return false;
                    }
                }
            }
        }
        console.error(`[StorageCleanup] Failed to save ${key}:`, error);
        return false;
    }
}

/**
 * Format bytes to human-readable size
 */
function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Log current storage usage to console (only if > 70% used)
 */
export function logStorageUsage(): void {
    const stats = getStorageStats();
    // Only log if storage is getting concerning
    if (stats.percentUsed > 70) {
        console.warn(`[Storage] ${stats.percentUsed}% used (${formatSize(stats.used)})`);
    }
}

export default {
    getStorageStats,
    cleanupNonCriticalStorage,
    emergencyCleanup,
    checkAndCleanupIfNeeded,
    safeSetItem,
    logStorageUsage
};

